import { supabase } from "@/integrations/supabase/client";
import { useAuthStore } from "../authStore";
import { useStoreManagementStore } from "../storeManagementStore";
import { ChatState, MessageType } from "./types";
import { trackEvent } from "@/services/analytics";
import {
  uploadChatAttachment,
  removeChatAttachment,
} from "@/services/chat/attachmentService";
import { fcmService } from "@/services/fcmService";
import { pushNotificationService } from "@/services/notifications/pushNotificationService";

export const createMessageOperations = (set, get: () => ChatState) => ({
  fetchMessages: async (storeId: string): Promise<void> => {
    const currentUser = useAuthStore.getState().user;
    if (!currentUser) {
      console.error("User not logged in");
      return;
    }

    set({ loading: true, currentStoreId: storeId });

    try {
      const { data: messages, error } = await supabase
        .from("chat_messages")
        .select(
          `
          *,
          reactions:chat_message_reactions(*)
        `
        )
        .eq("store_id", storeId)
        .order("created_at", { ascending: true });

      if (error) {
        console.error("Error fetching messages:", error);
      } else {
        set({ messages: messages || [], loading: false });

        // Track message fetch count in analytics
        trackEvent("chat_message_received", {
          store_id: storeId,
          message_count: messages?.length || 0,
        });
      }
    } catch (error) {
      set({ loading: false });
    }
  },

  initializeMessages: async (): Promise<void> => {
    const currentStoreId = get().currentStoreId;
    const { stores } = useStoreManagementStore.getState();

    // If we already have a store ID, fetch messages for it
    if (currentStoreId) {
      await get().fetchMessages(currentStoreId);
      return;
    }
    // If we don't have a current store ID but have stores, use the first one
    if (stores && stores.length > 0) {
      const firstStoreId = stores[0].id;
      set({ currentStoreId: firstStoreId });
      await get().fetchMessages(firstStoreId);
      return;
    }
  },

  sendMessage: async (
    storeId: string,
    content: string,
    type: MessageType = "text",
    attachmentUrl?: string
  ): Promise<boolean> => {
    try {
      const currentUser = useAuthStore.getState().user;
      if (!currentUser) {
        console.error("User not logged in");
        return false;
      }

      const senderName = currentUser.email;

      // Create the message
      const { data: message, error } = await supabase
        .from("chat_messages")
        .insert({
          store_id: storeId,
          sender_id: currentUser.id,
          sender_name: senderName,
          content,
          message_type: type,
          attachment_url: attachmentUrl,
        })
        .select()
        .single();

      if (error) {
        console.error("Error sending message:", error);
        return false;
      }

      const employees = await useStoreManagementStore
        .getState()
        .fetchEmployees(storeId);

      if (employees) {
        // Send push notification to each user
        for (const user of employees) {
          await fcmService.sendChatNotification(user.id, {
            messageId: message.id,
            content: message.content,
            senderName: senderName,
            senderId: currentUser.id,
            storeId: storeId,
            messageType: type,
          });
        }
        
        // Send push notification using the new service
        try {
          await pushNotificationService.notifyNewMessage(message, storeId, currentUser.id);
        } catch (error) {
          console.error('Error sending push notification for new message:', error);
        }
      }

      // Update messages in store
      await get().fetchMessages(storeId);

      // Track message sent in analytics
      trackEvent("chat_message_sent", {
        store_id: storeId,
        message_type: type,
        has_attachment: !!attachmentUrl,
      });

      return true;
    } catch (error) {
      console.error("Error in sendMessage:", error);
      return false;
    }
  },

  deleteMessage: async (messageId: string): Promise<boolean> => {
    try {
      // First, get the message to check if it has an attachment
      const { data: message, error: fetchError } = await supabase
        .from("chat_messages")
        .select("attachment_url")
        .eq("id", messageId)
        .single();

      if (fetchError) {
        console.error("Error fetching message for deletion:", fetchError);
        return false;
      }

      // If the message has an attachment, delete it from storage
      if (message?.attachment_url) {
        try {
          await removeChatAttachment(message.attachment_url);
        } catch (attachmentError) {
          console.warn(
            "Could not delete attachment, continuing with message deletion:",
            attachmentError
          );
        }
      }

      // Now mark the message as deleted
      const { error } = await supabase
        .from("chat_messages")
        .update({ is_deleted: true })
        .eq("id", messageId);

      if (error) {
        console.error("Error deleting message:", error);
        return false;
      }

      // Track message deletion in analytics
      trackEvent("chat_message_deleted", {
        store_id: get().currentStoreId,
        had_attachment: !!message?.attachment_url,
      });

      return true;
    } catch (error) {
      console.error("Error in deleteMessage:", error);
      return false;
    }
  },
});
